import { apiClient } from './client';
import { createCrudApi } from './crud';
import type { ApiSuccess } from '../types';

export type OrderStatus =
  | 'draft' | 'pending_approval' | 'approved' | 'in_manufacturing'
  | 'manufactured' | 'allocated' | 'dispatched' | 'delivered' | 'rejected' | 'cancelled';

export interface OrderItem {
  id: number;
  productId: number;
  quantity: number;
  unitPrice: number;
  gstPercent: number;
  amount: number;
  taxAmount: number;
  totalAmount: number;
  manufacturedQty: number;
  allocatedQty: number;
  dispatchedQty: number;
  deliveredQty: number;
}

export interface OrderDispatch {
  id: number;
  dispatchCode: string;
  warehouseId: number | null;
  dispatchDate: string | null;
  transporter: string | null;
  vehicleNo: string | null;
  docketNo: string | null;
  status: string;
  deliveredAt: string | null;
  receivedBy: string | null;
  podFileUrl: string | null;
}

export interface OrderHistory {
  id: number;
  fromStatus: string | null;
  toStatus: string;
  action: string;
  remarks: string | null;
  createdAt: string;
}

export interface DealerOrder {
  id: number;
  orderCode: string;
  dealerId: number;
  regionId: number | null;
  status: OrderStatus;
  orderDate: string | null;
  expectedDate: string | null;
  totalAmount: number;
  totalTax: number;
  grandTotal: number;
  remarks: string | null;
  rejectionReason: string | null;
  createdAt: string;
  paymentUtr: string | null;
  paymentDate: string | null;
  paymentAmount: number | null;
  paymentStatus: 'unpaid' | 'advance' | 'received';
  outstandingAmount: number;
  items?: OrderItem[];
  manufacturingJobs?: Array<{ id: number; jobCode: string; status: string; plannedQty: number; producedQty: number }>;
  allocations?: Array<{ id: number; productId: number; quantity: number; status: string; warehouseId: number | null; inventoryTxnId: number | null }>;
  dispatches?: OrderDispatch[];
  history?: OrderHistory[];
  confirmations?: Array<{ id: number; dispatchId: number; status: string; rating: number | null; createdAt: string }>;
}

export interface CompanyBankInfo {
  accountName: string;
  bankName: string;
  accountNumber: string;
  ifscCode: string;
  bankAddress: string;
}

export interface CreateOrderInput {
  dealerId: number;
  orderDate?: string;
  expectedDate?: string;
  remarks?: string;
  items: { productId: number; quantity: number }[];
}

export interface OrderAnalytics {
  totalOrders: number;
  totalValue: number;
  byStatus: Record<string, number>;
  pendingApproval: number;
  inFulfilment: number;
  delivered: number;
  open: number;
  recent: DealerOrder[];
}

const base = createCrudApi<DealerOrder, CreateOrderInput>('dealer-orders');

export const dealerOrdersApi = {
  ...base,
  async analytics(): Promise<OrderAnalytics> {
    const { data } = await apiClient.get<ApiSuccess<OrderAnalytics>>('/dealer-orders/analytics');
    return data.data;
  },
  async submit(id: number): Promise<DealerOrder> {
    return (await apiClient.post<ApiSuccess<DealerOrder>>(`/dealer-orders/${id}/submit`, {})).data.data;
  },
  async paymentInfo(): Promise<CompanyBankInfo> {
    return (await apiClient.get<ApiSuccess<CompanyBankInfo>>('/dealer-orders/payment-info')).data.data;
  },
  async savePayment(id: number, body: { utr: string; paymentDate: string; amount: number }): Promise<DealerOrder> {
    return (await apiClient.post<ApiSuccess<DealerOrder>>(`/dealer-orders/${id}/payment`, body)).data.data;
  },
  async approve(id: number): Promise<DealerOrder> {
    return (await apiClient.post<ApiSuccess<DealerOrder>>(`/dealer-orders/${id}/approve`, {})).data.data;
  },
  async reject(id: number, reason: string): Promise<DealerOrder> {
    return (await apiClient.post<ApiSuccess<DealerOrder>>(`/dealer-orders/${id}/reject`, { reason })).data.data;
  },
  async startManufacturing(id: number): Promise<DealerOrder> {
    return (await apiClient.post<ApiSuccess<DealerOrder>>(`/dealer-orders/${id}/manufacture/start`, {})).data.data;
  },
  async completeManufacturing(id: number): Promise<DealerOrder> {
    return (await apiClient.post<ApiSuccess<DealerOrder>>(`/dealer-orders/${id}/manufacture/complete`, {})).data.data;
  },
  async allocate(id: number, warehouseId: number): Promise<DealerOrder> {
    return (await apiClient.post<ApiSuccess<DealerOrder>>(`/dealer-orders/${id}/allocate`, { warehouseId })).data.data;
  },
  async dispatch(id: number, input: { transporter?: string; vehicleNo?: string; docketNo?: string; dispatchDate?: string }): Promise<DealerOrder> {
    return (await apiClient.post<ApiSuccess<DealerOrder>>(`/dealer-orders/${id}/dispatch`, input)).data.data;
  },
  async deliver(id: number, dispatchId: number, input: { receivedBy: string; podFileUrl?: string; remarks?: string }): Promise<DealerOrder> {
    return (await apiClient.post<ApiSuccess<DealerOrder>>(`/dealer-orders/${id}/dispatches/${dispatchId}/deliver`, input)).data.data;
  },
  async confirmDelivery(id: number, dispatchId: number, input: { status: 'confirmed' | 'disputed'; rating?: number; remarks?: string }): Promise<unknown> {
    return (await apiClient.post<ApiSuccess<unknown>>(`/dealer-orders/${id}/dispatches/${dispatchId}/confirm`, input)).data.data;
  },
};
